import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useFeatureFlag } from "@origin-digital/feature-flags";
import {
  Box,
  Column,
  Columns,
  Divider,
  Hidden,
  Stack,
  StackSection,
  Text,
} from "@origin-digital/ods-core";
import { CantFind } from "src/components/SearchResults";
import { CantFindSkeleton } from "src/components/SearchResults";
import { FilterChips } from "src/components/SearchResults";
import { FilterChipsSkeleton } from "src/components/SearchResults";
import { NoResults } from "src/components/SearchResults";
import { SearchPagination } from "src/components/SearchResults";
import { SearchResultsList } from "src/components/SearchResults";
import { SearchResultsSkeleton } from "src/components/SearchResults";
import { ErrorBoundary } from "src/components/ErrorBoundary/ErrorBoundary";
import { AIAnswer } from "src/components/SearchResults";
import { useSearchResultsPage } from "./hooks/useSearchResultsPage";
import { useConvoSearchContext } from "src/context/ConvoSearchContext";
import { PageTemplate } from "../PageTemplate/PageTemplate";
import { AISources } from "src/components/SearchResults/AISources";
import { AIAnswerFooter } from "src/components/SearchResults/AIAnswerFooter";
import { BottomAlignedBox } from "src/components/SearchResults/AIAnswer.styles";

export const SearchResultsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const term = searchParams.get("term") ?? "";
  const isPhase2 = useFeatureFlag("phase2");
  const isAiSearch = useFeatureFlag("aiSearch");

  const {
    tabs,
    results,
    pagination,
    relatedSearches,
    spellingSuggestion,
    error,
    selectedTab,
    showSkeleton,
    handleSelect,
    setCurrentPage,
  } = useSearchResultsPage();

  const { state, search, reset } = useConvoSearchContext();

  useEffect(() => {
    if (!term || !isAiSearch) return;
    search(term);
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const hasNoResults = !showSkeleton && !error && results.length === 0;
  // NEUTRAL queries can resolve to `{ decision: "NEUTRAL" }` with no answer
  // when sampled out of cohort or the quota bucket is exhausted. Gate on
  // presence of an answer so the AI Summary panel doesn't render empty.
  const hasAiAnswer = state.status === "done" && Boolean(state.answer);
  const showAI =
    isAiSearch &&
    !hasNoResults &&
    (state.status === "connecting" || hasAiAnswer);

  return (
    <PageTemplate data-id="search-results-page" isReady={!showSkeleton}>
      <StackSection
        data-id="search-results-content"
        width="xlarge"
        hideGutter={false}
        space="none"
        paddingY="none"
        backgroundColor="white"
      >
        <Box marginBottom={{ xs: "xxlarge", lg: "xxxlarge" }}>
          {isPhase2 && showAI && (
            <Box
              marginBottom={{ xs: "xxlarge", lg: "xxxlarge" }}
              data-id="ai-answer-section"
            >
              <Columns space="none" collapseBelow="md" alignY="stretch">
                <Column width="2/3">
                  <Box
                    marginRight={{ lg: "xxlarge", md: "xxlarge", xs: "none" }}
                    display="flex"
                    flexDirection="column"
                    height="full"
                    gap="none"
                  >
                    <AIAnswer
                      answer={state.answer}
                      status={state.status}
                      error={state.error}
                    />

                    <BottomAlignedBox below="lg">
                      <AIAnswerFooter
                        threadId={state.threadId}
                        messageId={state.messageId}
                        status={state.status}
                      />
                    </BottomAlignedBox>
                  </Box>
                </Column>
                <Column width="1/3">
                  <AISources
                    sources={state.sources}
                    status={state.status}
                    answer={state.answer}
                  />
                </Column>
              </Columns>
              <Hidden above="lg">
                <Box marginTop="xlarge">
                  <AIAnswerFooter
                    threadId={state.threadId}
                    messageId={state.messageId}
                    status={state.status}
                  />
                </Box>
              </Hidden>
              <Box paddingTop="large">
                <Divider />
              </Box>
            </Box>
          )}

          <Stack space={{ xs: "xlarge", lg: "xxlarge" }}>
            {showSkeleton ? (
              <FilterChipsSkeleton />
            ) : (
              <FilterChips
                tabs={tabs}
                selectedTab={selectedTab}
                onSelect={handleSelect}
              />
            )}
            {!showSkeleton && !error && results.length === 0 ? (
              <NoResults spellingSuggestion={spellingSuggestion} />
            ) : (
              <>
                {showSkeleton || relatedSearches.length > 0 ? (
                  <Columns space="large" collapseBelow="md">
                    <Column width="2/3">
                      <Box gap="medium">
                        <ErrorBoundary
                          fallback={
                            <Text>Something went wrong. Please try again.</Text>
                          }
                        >
                          {showSkeleton && <SearchResultsSkeleton />}
                          {!showSkeleton && !error && results.length > 0 && (
                            <SearchResultsList results={results} />
                          )}
                        </ErrorBoundary>
                        <Box marginY="xlarge">
                          {showSkeleton ? (
                            <CantFindSkeleton />
                          ) : (
                            <CantFind relatedSearches={relatedSearches} />
                          )}
                        </Box>
                      </Box>
                    </Column>
                    {/* Spacer to align results column with AI summary (2/3) above */}
                    <Column width="1/3"></Column>
                  </Columns>
                ) : (
                  <ErrorBoundary
                    fallback={
                      <Text>Something went wrong. Please try again.</Text>
                    }
                  >
                    {!error && results.length > 0 && (
                      <SearchResultsList results={results} />
                    )}
                  </ErrorBoundary>
                )}
                {!showSkeleton &&
                  !error &&
                  pagination?.pages &&
                  pagination.pages.length > 0 && (
                    <SearchPagination
                      pages={pagination.pages}
                      onSelect={setCurrentPage}
                    />
                  )}
              </>
            )}
          </Stack>
        </Box>
      </StackSection>
    </PageTemplate>
  );
};
